import {
  type CssPropertiesOverrides,
  type EditorElement,
  DATA,
} from '@wix/component-protocol/schema';
import type { CompRef } from '@wix/document-services-types';
import type {
  ContainerStyleOverrides,
  SimpleContainer,
} from '@wix/ambassador-devcenter-v1-component-type-data/types';
import { CssCustomPropertiesMerger } from '@wix/component-protocol/enricher';
import { AbstractInternalContainerOverride } from './AbstractInternalContainerOverride';
import {
  findFirstSimpleContainerDefinition,
  mapContainerLayoutToEditorElementLayout,
} from './utils';
import type { EditorPointer } from '@wix/editor-document-logic-api';

const CSS_PROPERTY_TYPE_TO_SIMPLE_CONTAINER_STYLE_KEY: Record<
  string,
  keyof ContainerStyleOverrides
> = {
  borderWidth: 'border',
  borderStyle: 'border',
  borderColor: 'border',
  borderTopWidth: 'border',
  borderTopStyle: 'border',
  borderTopColor: 'border',
  borderRightWidth: 'border',
  borderRightStyle: 'border',
  borderRightColor: 'border',
  borderBottomWidth: 'border',
  borderBottomStyle: 'border',
  borderBottomColor: 'border',
  borderLeftWidth: 'border',
  borderLeftStyle: 'border',
  borderLeftColor: 'border',
  borderInlineStartWidth: 'border',
  borderInlineStartStyle: 'border',
  borderInlineStartColor: 'border',
  borderInlineEndWidth: 'border',
  borderInlineEndStyle: 'border',
  borderInlineEndColor: 'border',
  borderTopLeftRadius: 'borderRadius',
  borderTopRightRadius: 'borderRadius',
  borderBottomRightRadius: 'borderRadius',
  borderBottomLeftRadius: 'borderRadius',
  borderStartStartRadius: 'borderRadius',
  borderStartEndRadius: 'borderRadius',
  borderEndStartRadius: 'borderRadius',
  borderEndEndRadius: 'borderRadius',
  boxShadow: 'shadow',
};

export class SimpleContainerOverride extends AbstractInternalContainerOverride {
  static containerStyleToStyleOverrides(
    style: SimpleContainer['style'],
    internalContainerDefinition: EditorElement['cssCustomProperties'],
  ): CssPropertiesOverrides {
    if (!style || !internalContainerDefinition) {
      return {};
    }

    const styleOverrides: CssPropertiesOverrides = {};
    for (const [propertyKey, propertyDefinition] of Object.entries(
      internalContainerDefinition,
    )) {
      if (!propertyDefinition) {
        continue;
      }

      const cssPropertyType = propertyDefinition.cssPropertyType;
      const styleKey = cssPropertyType
        ? CSS_PROPERTY_TYPE_TO_SIMPLE_CONTAINER_STYLE_KEY[cssPropertyType]
        : undefined;

      if (styleKey) {
        const styleItemOverride = style[styleKey];
        if (styleItemOverride) {
          styleOverrides[propertyKey] = styleItemOverride;
        }
      }
    }

    return styleOverrides;
  }

  static applySimpleContainerOverrides(
    containerDefinition: SimpleContainer,
    internalContainerDefinition: EditorElement,
    options?: { presetAwareLayout?: boolean },
  ): EditorElement {
    let updated = internalContainerDefinition;

    if (containerDefinition.behaviors) {
      const simpleEntry = findFirstSimpleContainerDefinition(updated);
      if (simpleEntry) {
        const [key, entry] = simpleEntry;
        if (entry[DATA.DATA_TYPE.container]?.simple) {
          updated = {
            ...updated,
            data: {
              ...updated.data,
              [key]: {
                ...entry,
                [DATA.DATA_TYPE.container]: {
                  ...entry[DATA.DATA_TYPE.container],
                  simple: containerDefinition,
                },
              },
            },
          };
        }
      }
    }

    if (containerDefinition.layout) {
      updated = {
        ...updated,
        layout: {
          ...(updated.layout ?? {}),
          ...(options?.presetAwareLayout
            ? mapContainerLayoutToEditorElementLayout(
                containerDefinition.layout,
              )
            : containerDefinition.layout),
        },
      };
    }

    if (containerDefinition.style) {
      let cssCustomProperties = updated.cssCustomProperties;
      if (cssCustomProperties) {
        const styleOverrides =
          SimpleContainerOverride.containerStyleToStyleOverrides(
            containerDefinition.style,
            cssCustomProperties,
          );
        const merger = new CssCustomPropertiesMerger({
          style: cssCustomProperties,
          styleOverrides,
        });
        const mergedStyle = merger.merge();
        cssCustomProperties = Object.keys(mergedStyle).reduce((acc, key) => {
          if (mergedStyle[key]?.deprecated) {
            // @ts-expect-error undefined drives deepMerge cleanup
            acc[key] = undefined;
          }
          return acc;
        }, mergedStyle);
        updated = { ...updated, cssCustomProperties };
      }
    }

    if (containerDefinition.displayName && updated.displayName) {
      updated = { ...updated, displayName: containerDefinition.displayName };
    }

    return updated;
  }

  resolve(
    editorPointer: EditorPointer<CompRef>,
    containerOwnerPointer: EditorPointer<CompRef>,
    editorElement: EditorElement,
  ): EditorElement | null {
    const definition = this.containerPlatformService.getManifestDefinition(
      editorPointer,
      containerOwnerPointer,
      'simple',
    );
    if (!definition) {
      return null;
    }
    return SimpleContainerOverride.applySimpleContainerOverrides(
      definition,
      editorElement,
      {
        presetAwareLayout: this.containerPlatformService.experimentsAPI.enabled(
          'dmSystemContainerMinWidth',
        ),
      },
    );
  }
}
